import { Trans, msg } from "@lingui/macro";

export const QUESTION_TREE = {
  orthodontics: {
    title: <Trans>Orthodontic Treatment Questions</Trans>,
    questions: [
      {
        id: "orthodonticHistory",
        type: "select",
        label: <Trans>Have you had orthodontic treatment before?</Trans>,
        options: [
          msg`No`,
          msg`Yes - braces`,
          msg`Yes - Invisalign`,
          msg`Yes - retainers`,
          msg`Yes - other`,
        ],
      },
      {
        id: "orthodonticGoals",
        type: "multiselect",
        label: <Trans>What are your main orthodontic goals?</Trans>,
        options: [
          msg`Straighten crooked teeth`,
          msg`Close gaps between teeth`,
          msg`Fix overbite/underbite`,
          msg`Improve bite alignment`,
          msg`Enhance smile aesthetics`,
          msg`Prepare for other dental work`,
        ],
      },
      {
        id: "treatmentDuration",
        type: "select",
        label: <Trans>What is your preferred treatment timeline?</Trans>,
        options: [
          msg`6-12 months`,
          msg`1-2 years`,
          msg`2+ years`,
          msg`Whatever is necessary`,
          msg`As fast as possible`,
        ],
      },
      {
        id: "appliancePreference",
        type: "select",
        label:  <Trans>Do you have a preference for type of braces?</Trans>,
        options: [
          msg`Traditional metal braces`,
          msg`Ceramic braces`,
          msg`Lingual braces`,
          msg`Clear aligners`,
          msg`No preference`,
        ],
      },
    ],
  },
  implants: {
    title: <Trans>Dental Implant Questions</Trans>,
    questions: [
      {
        id: "missingTeethCount",
        type: "select",
        label: <Trans>How many teeth are you missing?</Trans>,
        options: [
          msg`1 tooth`,
          msg`2-3 teeth`,
          msg`4-6 teeth`,
          msg`7+ teeth`,
          msg`Full arch (upper)`,
          msg`Full arch (lower)`,
          msg`Both arches`,
        ],
      },
      {
        id: "missingTeethDuration",
        type: "select",
        label: <Trans>How long have the teeth been missing?</Trans>,
        options: [
          msg`Less than 6 months`,
          msg`6 months - 1 year`,
          msg`1-2 years`,
          msg`2-5 years`,
          msg`5+ years`,
        ],
      },
      {
        id: "boneGraftHistory",
        type: "select",
        label: <Trans>Have you had bone grafting procedures?</Trans>,
        options: [
          msg`No`,
          msg`Yes - recent (within 6 months)`,
          msg`Yes - past`,
          msg`Unsure`,
          msg`Recommended but not done`,
        ],
      },
      {
        id: "implantExpectations",
        type: "multiselect",
        label: <Trans>What are your expectations for dental implants?</Trans>,
        options: [
          msg`Restore chewing function`,
          msg`Improve speech`,
          msg`Enhance appearance`,
          msg`Prevent bone loss`,
          msg`Avoid removable dentures`,
          msg`Long-term solution`,
        ],
      },
    ],
  },  
  cosmetic: {
    title: <Trans>Cosmetic Dentistry Questions</Trans>,
    questions: [
      {
        id: "cosmeticGoals",
        type: "multiselect",
        label: <Trans>What cosmetic improvements are you seeking?</Trans>,
        options: [
          msg`Whiter teeth`,
          msg`Straighter teeth`,
          msg`Close gaps`,
          msg`Fix chipped teeth`,
          msg`Improve gum line`,
          msg`Replace old fillings`,
          msg`Full smile makeover`,
        ],
      },
      {
        id: "previousCosmetic",
        type: "select",
        label: <Trans>Have you had cosmetic dental work before?</Trans>,
        options: [
          msg`No`,
          msg`Teeth whitening`,
          msg`Veneers`,
          msg`Bonding`,
          msg`Crowns`,
          msg`Multiple procedures`,
        ],
      },
      {
        id: "smileConfidence",
        type: "select",
        label: <Trans>How confident are you with your current smile?</Trans>,
        options: [
          msg`Very confident`,
          msg`Somewhat confident`,
          msg`Not very confident`,
          msg`Not confident at all`,
        ],
      },
      {
        id: "socialImpact",
        type: "select",
        label: <Trans>Does your smile affect your social or professional life?</Trans>,
        options: [
          msg`Not at all`,
          msg`Slightly`,
          msg`Moderately`,
          msg`Significantly`,
          msg`Very significantly`,
        ],
      },      
    ],
  },
  restoration: {
    title: <Trans>Full Mouth Reconstruction Questions</Trans>,
    questions: [
      {
        id: "reconstructionReason",
        type: "multiselect",
        label: <Trans>What issues require full mouth reconstruction?</Trans>,
        options: [
          msg`Severe tooth decay`,
          msg`Advanced gum disease`,
          msg`Trauma/injury`,
          msg`Severe wear/grinding`,
          msg`Failed previous dental work`,
          msg`Congenital conditions`,
          msg`TMJ disorders`,
        ],
      },
      {
        id: "currentPainLevel",
        type: "select",
        label: <Trans>What is your current pain/discomfort level?</Trans>,
        options: [
          msg`No pain`,
          msg`Mild discomfort`,
          msg`Moderate pain`,
          msg`Severe pain`,
          msg`Constant pain`,
        ],
      },
      {
        id: "functionIssues",
        type: "multiselect",
        label: <Trans>What functional problems are you experiencing?</Trans>,
        options: [
          msg`Difficulty chewing`,
          msg`Speech problems`,
          msg`Jaw pain/clicking`,
          msg`Teeth sensitivity`,
          msg`Frequent infections`,
          msg`Bad breath`,
          msg`Loose teeth`,
        ],
      },
      {
        id: "treatmentPriority",
        type: "select",
        label: <Trans>What is your main priority for treatment?</Trans>,
        options: [
          msg`Pain relief`,
          msg`Function restoration`,
          msg`Aesthetic improvement`,
          msg`Long-term health`,
          msg`All of the above`,
        ],
      },
    ],
  },
  veneers: {
    title: <Trans>Veneer Treatment Questions</Trans>,
    questions: [
      {
        id: "veneerGoals",
        type: "multiselect",
        label: <Trans>What improvements are you seeking with veneers?</Trans>,
        options: [
          msg`Whiter teeth`,
          msg`Straighter appearance`,
          msg`Close gaps between teeth`,
          msg`Repair chipped teeth`,
          msg`Change tooth shape`,
          msg`Create uniform smile`,
          msg`Cover stains or discoloration`,
        ],
      },
      {
        id: "teethToTreat",
        type: "select",
        label: <Trans>How many teeth are you considering for veneers?</Trans>,
        options: [
          msg`2-4 teeth`,
          msg`6-8 teeth`,
          msg`10-12 teeth`,
          msg`Full smile makeover`,
          msg`Not sure yet`,
        ],
      },
      {
        id: "currentTeethCondition",
        type: "select",
        label: <Trans>What is the current condition of your teeth?</Trans>,
        options: [
          msg`Healthy with minor cosmetic issues`,
          msg`Some chips or cracks`,
          msg`Staining or discoloration`,
          msg`Gaps between teeth`,
          msg`Multiple cosmetic concerns`,
        ],
      },
      {
        id: "previousCosmetic",
        type: "select",
        label: <Trans>Have you had cosmetic dental work before?</Trans>,
        options: [
          msg`No`,
          msg`Teeth whitening`,
          msg`Bonding`,
          msg`Veneers`,
          msg`Crowns`,
          msg`Multiple procedures`,
        ],
      },
    ],
  },
  crowns: {
    title: <Trans>Crown Treatment Questions</Trans>,
    questions: [
      {
        id: "crownReason",
        type: "multiselect",
        label: <Trans>Why do you need crowns?</Trans>,
        options: [
          msg`Protect damaged tooth`,
          msg`Restore broken tooth`,
          msg`Cover large filling`,
          msg`After root canal`,
          msg`Cosmetic improvement`,
          msg`Replace old crown`,
          msg`Strengthen weak tooth`,
        ],
      },
      {
        id: "numberOfCrowns",
        type: "select",
        label: <Trans>How many crowns do you need?</Trans>,
        options: [
          msg`1 crown`,
          msg`2-3 crowns`,
          msg`4-6 crowns`,
          msg`7+ crowns`,
          msg`Not sure`,
        ],
      },
      {
        id: "crownMaterial",
        type: "select",
        label: <Trans>Do you have a preference for crown material?</Trans>,
        options: [
          msg`Porcelain (most natural)`,
          msg`Porcelain-fused-to-metal`,
          msg`All-ceramic`,
          msg`Gold`,
          msg`No preference`,
        ],
      },
      {
        id: "urgencyLevel",
        type: "select",
        label: <Trans>How urgent is your need for treatment?</Trans>,
        options: [
          msg`Emergency - pain or broken tooth`,
          msg`Soon - within 1 month`,
          msg`Planning ahead - 2-3 months`,
          msg`Future planning - flexible timing`,
        ],
      },
    ],
  },
  whitening: {
    title: <Trans>Teeth Whitening Questions</Trans>,
    questions: [
      {
        id: "whiteningGoal",
        type: "select",
        label: <Trans>What level of whitening are you seeking?</Trans>,
        options: [
          msg`Light whitening - 2-3 shades`,
          msg`Moderate whitening - 4-6 shades`,
          msg`Dramatic whitening - 7+ shades`,
          msg`Just want to remove stains`,
          msg`Not sure`,
        ],
      },
      {
        id: "stainingType",
        type: "multiselect",
        label: <Trans>What type of staining do you have?</Trans>,
        options: [
          msg`Coffee/tea stains`,
          msg`Wine stains`,
          msg`Tobacco stains`,
          msg`Age-related yellowing`,
          msg`Medication staining`,
          msg`Fluorosis spots`,
          msg`General discoloration`,
        ],
      },
      {
        id: "previousWhitening",
        type: "select",
        label: <Trans>Have you tried teeth whitening before?</Trans>,
        options: [
          msg`No`,
          msg`Over-the-counter products`,
          msg`Professional whitening`,
          msg`Home whitening trays`,
          msg`Multiple attempts`,
        ],
      },
      {
        id: "sensitivityConcern",
        type: "select",
        label: <Trans>Do you have sensitive teeth?</Trans>,
        options: [
          msg`No sensitivity issues`,
          msg`Mild sensitivity`,
          msg`Moderate sensitivity`,
          msg`Severe sensitivity`,
          msg`Not sure`,
        ],
      },
    ],
  },
  other: {
    title: <Trans>General Dental Questions</Trans>,
    questions: [
      {
        id: "lastDentalVisit",
        type: "select",
        label: <Trans>When was your last dental visit?</Trans>,
        options: [
          msg`Within 6 months`,
          msg`6 months - 1 year`,
          msg`1-2 years`,
          msg`2-5 years`,
          msg`5+ years`,
          msg`Never`,
        ],
      },
      {
        id: "dentalConcerns",
        type: "multiselect",
        label: <Trans>What dental concerns do you have?</Trans>,
        options: [
          msg`Tooth pain`,
          msg`Gum problems`,
          msg`Bad breath`,
          msg`Teeth sensitivity`,
          msg`Cosmetic concerns`,
          msg`Bite problems`,
          msg`Preventive care`,
          msg`No specific concerns`,
        ],
      },
      {
        id: "treatmentInterest",
        type: "multiselect",
        label: <Trans>What treatments are you interested in learning about?</Trans>,
        options: [
          msg`Preventive care`,
          msg`Fillings/restorations`,
          msg`Teeth whitening`,
          msg`Orthodontics`,
          msg`Implants`,
          msg`Cosmetic dentistry`,
          msg`Gum treatment`,
          msg`Not sure yet`,
        ],
      },
    ],
  },  
};

